import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Typography } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  listPointState,
  selectedPoint,
  selectedPointDetail
} from 'store/buildingStore'
import { currentAnnotation } from 'store/krpanoStore'
import Utils from 'utils'
import {
  getPointVersion,
  handlePointVersionError,
  syncPointVersion
} from 'utils/pointVersion'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'

type Props = {
  handleCancelDelete: () => void
  handleCloseModal: () => void
}

export default function BCPDeleteAnnotationModal({
  handleCancelDelete,
  handleCloseModal
}: Props) {
  const { t } = useTranslation()
  const { id, floorId } = useParams()
  const { navigate } = useDirectory()

  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const currentSelected = useRecoilValue(currentAnnotation)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const setPoints = useSetRecoilState(listPointState)

  const { trigger: deleteAnnotation, isMutating } =
    useMutation(`v1/annotations`)

  const handleSubmit = () => {
    const krpano = document.getElementById('embedpano-full') as any
    const versionIdToSend = getPointVersion(currentSelectedPoint, pointDetail)
    !isMutating &&
      deleteAnnotation(
        {
          method: 'delete',
          query: [currentSelected?.id],
          headers: { 'X-Point-Version-ID': String(versionIdToSend) },
          params: {
            building_id: Number(id),
            floor_id: Number(floorId),
            point_id: currentSelectedPoint
          },
          data: null,
          successMessage: t('building.deleted')
        },
        {
          onSuccess: (data) => {
            syncPointVersion(
              data?.pointVersionId,
              currentSelectedPoint,
              setPointDetail,
              setPoints
            )
            setPointDetail(
              (prev) =>
                prev && {
                  ...prev,
                  annotations: prev?.annotations.filter(
                    (item) => item.id !== currentSelected?.id
                  )
                }
            )
            krpano.call(`remove_annotation(${currentSelected?.id});`)
            handleCancelDelete()
            handleCloseModal()
          },
          onError: (error) => {
            handleCancelDelete()
            handleCloseModal()
            if (handlePointVersionError(error, t)) {
              return
            }
            Utils.handleErrorNavigate(error, navigate)
          }
        }
      )
  }

  return (
    <Modal
      width={440}
      isEnterable
      onEnter={handleSubmit}
      onCancel={handleCancelDelete}
    >
      <Title title={t('common.titleDeleteAnnotation')} bottomMargin="mb-8" />

      <div className="flex flex-col gap-10 text-center">
        <Typography className="text-[14px] leading-[21px]">
          {t('building.deleteConfirmation')}
        </Typography>
        <div className="flex gap-10 justify-center">
          <Button sizing="w-[140px] h-[50px]" onClick={handleCloseModal}>
            {t('building.buttonCancel')}
          </Button>

          <Button
            sizing="w-[140px] h-[50px]"
            className="bg-[#B80808]"
            onClick={handleSubmit}
          >
            {t('common.delete')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
